/**
 * Propaga edição de peça (descrição, qtd, medida, material) para todos os cards
 * do mesmo produto (outros lotes).
 * Checkbox (selecionado) NÃO entra nesta propagação — só no mesmo lote (ver callers).
 */

import { nomesProdutoIguais } from './autoCardsTempoCorte';
import { cardTempoCortePertenceAoLote } from './tempoProducaoPecaLote';

export type PecaPropagavel = {
  id?: string;
  numero?: number;
  descricao?: string;
  qtd?: number;
  medida?: string;
  material?: string;
  metrosQuadrados?: number;
  selecionado?: boolean;
  produtoOrigem?: string;
  [key: string]: unknown;
};

function parseNumeroFlex(valor: unknown): number {
  if (typeof valor === 'number') return Number.isFinite(valor) ? valor : 0;
  const texto = String(valor ?? '').trim();
  if (!texto) return 0;
  const normalizado = texto.replace(/\s+/g, '').replace(/\.(?=\d{3}(?:\D|$))/g, '').replace(',', '.');
  const n = Number(normalizado);
  return Number.isFinite(n) ? n : 0;
}

function calcularM2(medida: string, qtd: number): number {
  const partes = String(medida ?? '')
    .toLowerCase()
    .split(/[x×*]/)
    .map((p) => parseNumeroFlex(p))
    .filter((n) => n > 0);
  if (partes.length < 2 || qtd <= 0) return 0;
  return partes.reduce((acc, n) => acc * n, 1) * qtd;
}

function normChavePeca(s: string): string {
  return String(s || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toUpperCase()
    .replace(/\s+/g, ' ')
    .trim();
}

function chaveMedidaMaterial(medida: string, material: string): string {
  return `${normChavePeca(medida).replace(/\s+/g, '')}|${normChavePeca(material)}`;
}

/** Localiza a peça correspondente no card destino (número, índice ou medida+material). */
function acharIndicePecaCorrespondente(
  itensDestino: PecaPropagavel[],
  pecaOrigem: PecaPropagavel,
  indexOrigem: number
): number {
  const num = Number(pecaOrigem.numero) || 0;
  if (num > 0) {
    const byNum = itensDestino.findIndex((i) => Number(i.numero) === num);
    if (byNum >= 0) return byNum;
  }
  if (indexOrigem >= 0 && indexOrigem < itensDestino.length) return indexOrigem;

  const desc = normChavePeca(String(pecaOrigem.descricao || ''));
  if (desc) {
    const byDesc = itensDestino.findIndex((i) => normChavePeca(String(i.descricao || '')) === desc);
    if (byDesc >= 0) return byDesc;
  }

  const mm = chaveMedidaMaterial(String(pecaOrigem.medida || ''), String(pecaOrigem.material || ''));
  if (mm !== '|') {
    const byMm = itensDestino.findIndex(
      (i) => chaveMedidaMaterial(String(i.medida || ''), String(i.material || '')) === mm
    );
    if (byMm >= 0) return byMm;
  }
  return -1;
}

export type CamposPecaPropagados = 'descricao' | 'qtd' | 'medida' | 'material';

/**
 * Aplica os campos editados de uma peça a todos os cards com o mesmo nome de produto.
 * Não altera o card com `excetoCardId` (opcional).
 * Não propaga `selecionado` — isso é só no mesmo lote (UI).
 */
export function propagarCamposPecaEntreCardsMesmoProduto<
  T extends { id?: string; nome?: string; itens?: PecaPropagavel[] },
>(
  cards: T[],
  nomeProduto: string,
  pecaOrigem: PecaPropagavel,
  indexOrigem: number,
  campos: CamposPecaPropagados[],
  excetoCardId?: string
): { cards: T[]; alterados: number } {
  if (!String(nomeProduto || '').trim() || campos.length === 0) return { cards, alterados: 0 };

  let alterados = 0;
  const next = cards.map((card) => {
    if (excetoCardId && String(card.id || '') === String(excetoCardId)) return card;
    if (!nomesProdutoIguais(String(card.nome || ''), nomeProduto)) return card;

    const itens = Array.isArray(card.itens) ? [...card.itens] : [];
    if (itens.length === 0) return card;

    const idx = acharIndicePecaCorrespondente(itens, pecaOrigem, indexOrigem);
    if (idx < 0) return card;

    const atual = { ...itens[idx] };
    let mudou = false;
    for (const campo of campos) {
      const novo = pecaOrigem[campo];
      if (atual[campo] !== novo) {
        (atual as Record<string, unknown>)[campo] = novo;
        mudou = true;
      }
    }
    if (campos.includes('qtd') || campos.includes('medida')) {
      const qtd = parseNumeroFlex(atual.qtd);
      const medida = String(atual.medida ?? '').trim();
      const m2 = calcularM2(medida, qtd);
      if (m2 > 0 && atual.metrosQuadrados !== m2) {
        atual.metrosQuadrados = m2;
        mudou = true;
      }
    }
    if (!mudou) return card;
    itens[idx] = atual;
    alterados += 1;
    return { ...card, itens };
  });

  return { cards: next, alterados };
}

/**
 * Substitui a lista de peças de todos os cards do mesmo produto (ao salvar/gerar card).
 * Medida/qtd/descrição/material: sincroniza em todos os lotes.
 * Checkbox (selecionado): só no mesmo lote; outros lotes mantêm a marcação local.
 */
export function sincronizarListaPecasProdutoEntreCards<
  T extends { id?: string; nome?: string; lote?: string; itens?: PecaPropagavel[] },
>(
  cards: T[],
  nomeProduto: string,
  itensModelo: PecaPropagavel[],
  excetoCardId?: string,
  loteOrigem?: string
): T[] {
  if (!String(nomeProduto || '').trim() || !Array.isArray(itensModelo)) return cards;
  const loteRef = String(loteOrigem || '').trim();

  return cards.map((card) => {
    if (excetoCardId && String(card.id || '') === String(excetoCardId)) return card;
    if (!nomesProdutoIguais(String(card.nome || ''), nomeProduto)) return card;

    const mesmoLote =
      !loteRef ||
      cardTempoCortePertenceAoLote(card, loteRef) ||
      String(card.lote || '').trim().toLowerCase() === loteRef.toLowerCase();

    const itensAntigos = Array.isArray(card.itens) ? card.itens : [];
    const novos = itensModelo.map((modelo, i) => {
      const idx = acharIndicePecaCorrespondente(itensAntigos, modelo, i);
      const antigo = idx >= 0 ? itensAntigos[idx] : undefined;
      const qtd = parseNumeroFlex(modelo.qtd);
      const medida = String(modelo.medida ?? '').trim();
      const m2Calc = calcularM2(medida, qtd);
      const selecionado = mesmoLote
        ? modelo.selecionado !== false
        : antigo
          ? antigo.selecionado !== false
          : true;
      return {
        ...modelo,
        id: String(antigo?.id || modelo.id || `${Date.now()}-${i}`),
        numero: i + 1,
        selecionado,
        metrosQuadrados: m2Calc > 0 ? m2Calc : Number(modelo.metrosQuadrados) || 0,
      };
    });
    return { ...card, itens: novos };
  });
}
